import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Receipt, Eye, Filter } from 'lucide-react';
import { receiptApi } from '../lib/api';
import { formatDate, formatCurrency } from '../utils/format';

export default function Receipts() {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['receipts', dateFrom, dateTo],
    queryFn: () =>
      receiptApi.list({
        from: dateFrom || undefined,
        to: dateTo || undefined,
      }),
  });

  const receipts = data?.data?.receipts || [];

  const { data: statsData } = useQuery({
    queryKey: ['receipt-stats', dateFrom, dateTo],
    queryFn: () =>
      receiptApi.getStats({
        from: dateFrom || undefined,
        to: dateTo || undefined,
      }),
  });

  const stats = statsData?.data?.stats;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Receipts</h1>
        <p className="text-gray-600">View and manage all your receipts</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card">
            <p className="text-sm text-gray-600">Total Receipts</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total_receipts}</p>
          </div>
          <div className="card">
            <p className="text-sm text-gray-600">Total Amount</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(stats.total_amount || 0)}
            </p>
          </div>
          <div className="card">
            <p className="text-sm text-gray-600">Average Amount</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(stats.avg_amount || 0)}
            </p>
          </div>
          <div className="card">
            <p className="text-sm text-gray-600">Total Tax</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(stats.total_tax || 0)}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="flex items-center gap-4">
          <Filter className="h-5 w-5 text-gray-400" />
          <div className="flex gap-4 flex-1">
            <div>
              <label className="label">From Date</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="input"
              />
            </div>
            <div>
              <label className="label">To Date</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Receipts List */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Vendor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Payment
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {receipts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <Receipt className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No receipts found</p>
                  </td>
                </tr>
              ) : (
                receipts.map((receipt: any) => (
                  <tr key={receipt.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(receipt.transaction_date)}
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {receipt.vendor_name || 'Unknown'}
                        </p>
                        <p className="text-xs text-gray-500">{receipt.filename}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm font-semibold text-gray-900">
                        {formatCurrency(receipt.total_amount)}
                      </p>
                      {receipt.tax_amount > 0 && (
                        <p className="text-xs text-gray-500">
                          Tax: {formatCurrency(receipt.tax_amount)}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        {receipt.payment_method && (
                          <span className="badge-gray capitalize">
                            {receipt.payment_method}
                          </span>
                        )}
                        {receipt.check_number && (
                          <p className="text-xs text-gray-500 mt-1">
                            Check #{receipt.check_number}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <Link
                        to={`/documents/${receipt.document_id}`}
                        className="text-primary-600 hover:text-primary-900 inline-flex items-center gap-1"
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

